import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { listStateAndListMore } from '@/components/FListFooter';
import { decompile } from '@freelog/nmr_translator';
import type {
  InformalNodeManagerPageModelState,
  IRules,
  ChangeAction,
  FetchExhibitListAction,
  OnClickExhibitsAddBtnAction,
  OnClickExhibitsReplaceBtnAction,
  OnChangeExhibitTypeAction,
  OnChangeExhibitStatusAction,
  OnChangeExhibitKeywordsAction,
  OnLoadMoreExhibitsAction,
  OnClick_Exhibits_DeleteBtn_Action,
  OnChange_Exhibits_StatusSwitch_Action,
  RuleMatchAndResultReturn,
} from './types';
import { ruleMatchAndResult, mergeAuthInfoToList } from './helpers';

interface RuleMatchAndResultParams {
  nodeID: number;
  isRematch?: boolean;
}

export const exhibitEffects = {
  * fetchExhibitList({ payload }: FetchExhibitListAction, {
    call,
    select,
    put,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_ListMore: 'loading',
      },
    });

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: payload.isRematch !== undefined ? payload.isRematch : true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);

    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_PageError: '匹配失败',
        },
      });
      return;
    }

    let list: InformalNodeManagerPageModelState['exhibit_List'] = [];
    if (!payload.isRestart) {
      list = [
        ...informalNodeManagerPage.exhibit_List,
      ];
    }

    const resourceTypes: Array<string | number> = informalNodeManagerPage.exhibit_ResourceTypeCodes.labels.filter((rt) => {
      return rt !== '全部';
    });

    const params: Parameters<typeof FServiceAPI.InformalNode.testResources>[0] = {
      skip: list.length,
      limit: 50,
      nodeId: informalNodeManagerPage.node_ID,
      onlineStatus: Number(informalNodeManagerPage.exhibit_SelectedStatus) as 2,
      omitResourceType: '主题',
      resourceType: resourceTypes.length === 0 ? undefined : String(resourceTypes[resourceTypes.length - 1]),
      keywords: informalNodeManagerPage.exhibit_FilterKeywords || undefined,
    };

    const { data: data_informalExhibits } = yield call(FServiceAPI.InformalNode.testResources, params);

    const params1: Parameters<typeof mergeAuthInfoToList>[0] = {
      nodeID: informalNodeManagerPage.node_ID,
      list: data_informalExhibits.dataList,
    };

    const listWithAuth: any[] = yield call(mergeAuthInfoToList, params1);

    const exhibitList: InformalNodeManagerPageModelState['exhibit_List'] = [
      ...list,
      ...listWithAuth,
    ];

    const { state, more } = listStateAndListMore({
      list_Length: exhibitList.length,
      total_Length: data_informalExhibits.totalItem,
      has_FilterCriteria: informalNodeManagerPage.exhibit_ResourceTypeCodes.values[0] !== '#all'
        || informalNodeManagerPage.exhibit_SelectedStatus !== '2'
        || informalNodeManagerPage.exhibit_FilterKeywords !== '',
    });
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        exhibit_List: exhibitList,
        exhibit_ListTotal: data_informalExhibits.totalItem,
        exhibit_ListState: state,
        exhibit_ListMore: more,
      },
    });
  },
  * onClickExhibitsAddBtn({}: OnClickExhibitsAddBtnAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { addExhibitDrawer_Visible: true },
    });
  },
  * onClickExhibitsReplaceBtn({}: OnClickExhibitsReplaceBtnAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { replaceModal_Visible: true },
    });
  },
  * onChangeExhibitType({ payload }: OnChangeExhibitTypeAction, { put }: EffectsCommandMap) {

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        exhibit_ResourceTypeCodes: payload.value,
      },
    });

    yield put<FetchExhibitListAction>({
      type: 'fetchExhibitList',
      payload: {
        isRematch: false,
        isRestart: true,
      },
    });

  },
  * onChangeExhibitStatus({ payload }: OnChangeExhibitStatusAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { exhibit_SelectedStatus: payload.value as '0' },
    });
    yield put<FetchExhibitListAction>({
      type: 'fetchExhibitList',
      payload: {
        isRematch: false,
        isRestart: true,
      },
    });
  },
  * onChangeExhibitKeywords({ payload }: OnChangeExhibitKeywordsAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: { exhibit_FilterKeywords: payload.value },
    });
    yield put<FetchExhibitListAction>({
      type: 'fetchExhibitList',
      payload: {
        isRematch: false,
        isRestart: true,
      },
    });
  },
  * onLoadMoreExhibits({}: OnLoadMoreExhibitsAction, { put }: EffectsCommandMap) {
    yield put<FetchExhibitListAction>({
      type: 'fetchExhibitList',
      payload: {
        isRematch: false,
        isRestart: false,
      },
    });
  },
  * onClick_Exhibits_DeleteBtn({ payload }: OnClick_Exhibits_DeleteBtn_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informalNodeManagerPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    needHandleRules = needHandleRules.filter((nhr) => {
      return nhr.exhibitName !== payload.testResourceName;
    });

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_PageError: '匹配失败',
        },
      });
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        exhibit_List: informalNodeManagerPage.exhibit_List.filter((el) => {
          return el.testResourceId !== payload.testResourceId;
        }),
        exhibit_ListTotal: informalNodeManagerPage.exhibit_ListTotal - 1,
      },
    });

  },
  * onChange_Exhibits_StatusSwitch({ payload }: OnChange_Exhibits_StatusSwitch_Action, {
    select,
    call,
    put,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informalNodeManagerPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    if (needHandleRules.some((nhr) => nhr.exhibitName === payload.testResourceName && nhr.operation !== 'activate_theme')) {
      needHandleRules = needHandleRules.map((nhr) => {
        if (nhr.exhibitName === payload.testResourceName && nhr.operation !== 'activate_theme') {
          return {
            ...nhr,
            actions: [
              ...nhr.actions,
              {
                operation: 'online',
                content: payload.checked,
              },
            ],
          };
        }

        return nhr;
      });
    } else {
      const alterRule: IRules['alter'] = {
        operation: 'alter',
        exhibitName: payload.testResourceName,
        actions: [
          {
            operation: 'online',
            content: payload.checked,
          },
        ],
        text: '',
      };
      needHandleRules = [
        ...needHandleRules,
        alterRule,
      ];
    }

    needHandleRules.sort((a) => {
      return a.exhibitName === payload.testResourceName ? -1 : 0;
    });

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);
    if (result.status === 2) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_PageError: '匹配失败',
        },
      });
      return;
    }

    const params3: Parameters<typeof FServiceAPI.InformalNode.testResourceDetails>[0] = {
      testResourceId: payload.testResourceId,
    };

    const { data: data3 } = yield call(FServiceAPI.InformalNode.testResourceDetails, params3);

    const params1: Parameters<typeof mergeAuthInfoToList>[0] = {
      nodeID: informalNodeManagerPage.node_ID,
      list: [data3],
    };
    const [data1] = yield call(mergeAuthInfoToList, params1);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        exhibit_List: informalNodeManagerPage.exhibit_List.map((el) => {
          if (el.testResourceId === payload.testResourceId) {
            return data1;
          }
          return el;
        }),
      },
    });
  },
};
